import React, { useState } from 'react'
import styled from '@emotion/styled'
import { FaChevronCircleDown, FaChevronCircleRight } from 'react-icons/fa'
import { startCase } from 'lodash'

import {
  FlexContainer,
  Loader,
  XSpacing,
  YSpacing,
  DividerLine,
} from '@components/common'
import {
  ClearableInput,
  Input,
  TextArea,
  QuantityInput,
  Button,
} from '@components/common/form'

import {
  EventProposalPDFOverrideParams,
  EventProposalPDFParams,
  MenuItemGroupParams,
  OverrideMenuItemGroupParams,
  OverrideMenuItemParams,
} from '@types'
import { usePDFields } from '@hooks'
import { colors } from '../../../constants'

import services from '@services'
import coordinators from '@coordinators'
import { HandleError } from '@coordinators/composed'
const {
  LoadEventProposalPDFParams,
  SaveProposalPDFOverrideParams,
  CloseEditPDFModal,
} = coordinators
const { RestService, UIService, EditPDFService } = services

const loadEventProposalPDFParams = LoadEventProposalPDFParams({
  RestService,
  HandleError,
})
const saveProposalPDFOverrideParams = SaveProposalPDFOverrideParams({
  RestService,
  HandleError,
})
const closeEditPDFModal = CloseEditPDFModal({
  UIService,
  EditPDFService,
})
const { displayFailureMessage } = UIService.FlashMessage

const pricingFields: (keyof EventProposalPDFOverrideParams)[] = [
  'totalMenuPrice',
  'totalDrinkPrice',
  'totalRentalPrice',
  'totalDecorationPrice',
  'totalStaffingPrice',
  'totalAdditionalVendorsPrice',
  'subTotal',
  'serviceFee',
  'deliveryFee',
  'tip',
  'tax',
  'total',
]

type ServiceItemGroupKey =
  | 'drinkText'
  | 'rentalText'
  | 'decorationText'
  | 'staffingText'
  | 'additionalVendorsText'
const serviceItemGroups: ServiceItemGroupKey[] = [
  'drinkText',
  'rentalText',
  'decorationText',
  'staffingText',
  'additionalVendorsText',
]

interface EditEventProposalPDFModalProps {
  proposalId: string
}

export const EditEventProposalPDFModal: React.FC<
  EditEventProposalPDFModalProps
> = ({ proposalId }) => {
  const { fields, overrideFields, loading, setOverrideFields, getFieldValue } =
    usePDFields<EventProposalPDFParams, EventProposalPDFOverrideParams>({
      objectId: proposalId,
      paramsLoader: loadEventProposalPDFParams,
    })

  const onFieldChange =
    (field: keyof EventProposalPDFOverrideParams) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setOverrideFields({ ...overrideFields, [field]: e.target.value })
    }
  const onQuantityFieldChange =
    (field: keyof EventProposalPDFOverrideParams) => (value: number) => {
      setOverrideFields({ ...overrideFields, [field]: value })
    }

  const onClearField = (field: keyof EventProposalPDFOverrideParams) => {
    const nextOverrideFields = { ...overrideFields }
    delete nextOverrideFields[field]
    setOverrideFields(nextOverrideFields)
  }

  const onChangeContactField =
    (field: 'name' | 'title') => (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target
      setOverrideFields({
        ...overrideFields,
        contact: { ...(overrideFields?.contact || {}), [field]: value },
      })
    }
  const onClearContactField = (field: 'name' | 'title') => {
    const nextOverrideFields = { ...overrideFields }
    delete nextOverrideFields.contact?.[field]
    if (Object.keys(nextOverrideFields.contact || {}).length === 0) {
      delete nextOverrideFields.contact
    }
    setOverrideFields(nextOverrideFields)
  }
  const getContactFieldDisplayValue = (field: 'name' | 'title') => {
    const contact =
      getFieldValue<EventProposalPDFOverrideParams['contact']>('contact')

    return contact?.[field] || ''
  }

  const onChangeSalesRepField =
    (field: 'name' | 'email' | 'phoneNumber') =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target
      setOverrideFields({
        ...overrideFields,
        salesRep: { ...(overrideFields?.salesRep || {}), [field]: value },
      })
    }
  const onClearSalesRepField = (field: 'name' | 'email' | 'phoneNumber') => {
    const nextOverrideFields = { ...overrideFields }
    delete nextOverrideFields.salesRep?.[field]
    if (Object.keys(nextOverrideFields.salesRep || {}).length === 0) {
      delete nextOverrideFields.salesRep
    }
    setOverrideFields(nextOverrideFields)
  }
  const getSalesRepFieldDisplayValue = (
    field: 'name' | 'email' | 'phoneNumber',
  ) => {
    const salesRep =
      getFieldValue<EventProposalPDFOverrideParams['salesRep']>('salesRep')

    return salesRep?.[field] || ''
  }

  const getOverrideMenuItemGroupFields = (
    menuItemGroupType: keyof EventProposalPDFParams['menuItemsMap'],
  ) => {
    return overrideFields?.menuItemsMap?.[menuItemGroupType] || undefined
  }

  const onChangeMenuItemGroupField =
    (menuItemGroupType: keyof EventProposalPDFParams['menuItemsMap']) =>
    (field: keyof OverrideMenuItemGroupParams) =>
    (value: string | Record<string, OverrideMenuItemParams>) => {
      setOverrideFields({
        ...overrideFields,
        menuItemsMap: {
          ...(overrideFields?.menuItemsMap || {}),
          [menuItemGroupType]: {
            ...(overrideFields?.menuItemsMap?.[menuItemGroupType] || {}),
            [field]: value,
          },
        },
      })
    }
  const onClearMenuItemGroupField =
    (menuItemGroupType: keyof EventProposalPDFParams['menuItemsMap']) =>
    (field: keyof OverrideMenuItemGroupParams) => {
      const nextOverrideFields = { ...overrideFields }
      delete nextOverrideFields.menuItemsMap?.[menuItemGroupType]?.[field]
      if (
        Object.keys(nextOverrideFields.menuItemsMap?.[menuItemGroupType] || {})
          .length === 0
      ) {
        delete nextOverrideFields.menuItemsMap?.[menuItemGroupType]
      }
      setOverrideFields(nextOverrideFields)
    }
  const onChangeMenuItemField =
    (menuItemGroupType: keyof EventProposalPDFParams['menuItemsMap']) =>
    (itemId: string) =>
    (field: keyof OverrideMenuItemParams) =>
    (value: string) => {
      setOverrideFields({
        ...overrideFields,
        menuItemsMap: {
          ...(overrideFields?.menuItemsMap || {}),
          [menuItemGroupType]: {
            ...(overrideFields?.menuItemsMap?.[menuItemGroupType] || {}),
            items: {
              ...(overrideFields?.menuItemsMap?.[menuItemGroupType]?.items ||
                {}),
              [itemId]: {
                ...(overrideFields?.menuItemsMap?.[menuItemGroupType]?.items?.[
                  itemId
                ] || {}),
                [field]: value,
              },
            },
          },
        },
      })
    }
  const onClearMenuItemField =
    (menuItemGroupType: keyof EventProposalPDFParams['menuItemsMap']) =>
    (itemId: string) =>
    (field: keyof OverrideMenuItemParams) => {
      const nextOverrideFields = { ...overrideFields }
      delete nextOverrideFields.menuItemsMap?.[menuItemGroupType]?.items?.[
        itemId
      ]?.[field]
      if (
        Object.keys(
          nextOverrideFields.menuItemsMap?.[menuItemGroupType]?.items?.[
            itemId
          ] || {},
        ).length === 0
      ) {
        delete nextOverrideFields.menuItemsMap?.[menuItemGroupType]?.items?.[
          itemId
        ]
      }
      if (
        Object.keys(nextOverrideFields.menuItemsMap?.[menuItemGroupType] || {})
          .length === 0
      ) {
        delete nextOverrideFields.menuItemsMap?.[menuItemGroupType]
      }
      if (Object.keys(nextOverrideFields.menuItemsMap || {}).length === 0) {
        delete nextOverrideFields.menuItemsMap
      }
      setOverrideFields(nextOverrideFields)
    }

  const onToggleHiddenMenuItem = (itemId: string) => {
    const nextHiddenMenuItems = [...(overrideFields?.hiddenMenuItems || [])]
    if (nextHiddenMenuItems.includes(itemId)) {
      nextHiddenMenuItems.splice(nextHiddenMenuItems.indexOf(itemId), 1)
    } else {
      nextHiddenMenuItems.push(itemId)
    }

    const nextOverrideFields = { ...overrideFields }
    if (nextHiddenMenuItems.length === 0) {
      delete nextOverrideFields.hiddenMenuItems
    } else {
      nextOverrideFields.hiddenMenuItems = nextHiddenMenuItems
    }

    setOverrideFields(nextOverrideFields)
  }

  const onSaveOverrideFields = async () => {
    const success = await saveProposalPDFOverrideParams(
      proposalId,
      overrideFields,
    )
    if (success) {
      closeEditPDFModal()
    } else {
      displayFailureMessage(
        'Failed to save override fields. Please reach out to tech team for assistance.',
      )
    }
  }

  if (loading || !fields) {
    return (
      <FlexContainer justifyContent="center" alignItems="center" width="100%">
        <Loader width="100px" isCenter />
      </FlexContainer>
    )
  }

  return (
    <div>
      <InfoCopy>
        Click X&apos;s next to fields to revert to the proposal&apos;s current
        values.
      </InfoCopy>
      <SectionTitle>
        <span>1</span>Proposal Details
      </SectionTitle>
      <DividerLine margin="20px 0" height="2px" />
      <FlexContainer
        width="100%"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <ClearableInput
          width="30%"
          changed={Boolean(overrideFields?.accountName)}
          input={
            <Input
              label="Account Name"
              placeholder={fields?.accountName}
              value={getFieldValue<string>('accountName') || ''}
              onChange={onFieldChange('accountName')}
              disabled={true}
            />
          }
          onClear={() => onClearField('accountName')}
        />
        <ClearableInput
          width="30%"
          changed={Boolean(overrideFields?.setupDate)}
          input={
            <Input
              label="Setup Date"
              placeholder={fields?.setupDate}
              value={getFieldValue<string>('setupDate') || ''}
              onChange={onFieldChange('setupDate')}
              disabled={true}
            />
          }
          onClear={() => onClearField('setupDate')}
        />
        <ClearableInput
          width="30%"
          changed={Boolean(overrideFields?.setupTime)}
          input={
            <Input
              label="Setup Time"
              placeholder={fields?.setupTime}
              value={getFieldValue<string>('setupTime') || ''}
              onChange={onFieldChange('setupTime')}
              disabled={true}
            />
          }
          onClear={() => onClearField('setupTime')}
        />
      </FlexContainer>
      <YSpacing height="10x" />
      <FlexContainer
        width="100%"
        flexDirection="row"
        justifyContent="flex-start"
        alignItems="center"
      >
        <ClearableInput
          width="30%"
          changed={Boolean(overrideFields?.headCount)}
          input={
            <QuantityInput
              label="Head Count"
              pattern="[0-9]*"
              placeholder={fields?.headCount?.toString()}
              value={getFieldValue<number>('headCount')}
              onChange={onQuantityFieldChange('headCount')}
              disabled={true}
            />
          }
          onClear={() => onClearField('headCount')}
        />
        <XSpacing width="5%" />
        <ClearableInput
          width="30%"
          changed={Boolean(overrideFields?.mealsDonated)}
          input={
            <QuantityInput
              label="Meals Donated"
              pattern="[0-9]*"
              placeholder={fields?.mealsDonated?.toString()}
              value={getFieldValue<number>('mealsDonated')}
              onChange={onQuantityFieldChange('mealsDonated')}
              disabled={true}
            />
          }
          onClear={() => onClearField('mealsDonated')}
        />
      </FlexContainer>
      <DividerLine margin="20px 0" height="2px" />
      <FlexContainer
        width="100%"
        flexDirection="row"
        justifyContent="flex-start"
        alignItems="center"
      >
        <ClearableInput
          width="30%"
          changed={Boolean(overrideFields?.contact?.name)}
          input={
            <Input
              label="Contact Name"
              placeholder={fields?.contact?.name}
              value={getContactFieldDisplayValue('name')}
              onChange={onChangeContactField('name')}
              disabled={true}
            />
          }
          onClear={() => onClearContactField('name')}
        />
        <XSpacing width="5%" />
        <ClearableInput
          width="30%"
          changed={Boolean(overrideFields?.contact?.title)}
          input={
            <Input
              label="Contact Title"
              placeholder={fields?.contact?.title}
              value={getContactFieldDisplayValue('title')}
              onChange={onChangeContactField('title')}
              disabled={true}
            />
          }
          onClear={() => onClearContactField('title')}
        />
      </FlexContainer>
      <DividerLine margin="20px 0" height="2px" />
      <FlexContainer
        width="100%"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <ClearableInput
          width="30%"
          changed={Boolean(overrideFields?.salesRep?.name)}
          input={
            <Input
              label="Sales Rep Name"
              placeholder={fields?.salesRep?.name}
              value={getSalesRepFieldDisplayValue('name')}
              onChange={onChangeSalesRepField('name')}
              disabled={true}
            />
          }
          onClear={() => onClearSalesRepField('name')}
        />
        <ClearableInput
          width="30%"
          changed={Boolean(overrideFields?.salesRep?.email)}
          input={
            <Input
              label="Sales Rep Email"
              placeholder={fields?.salesRep?.email}
              value={getSalesRepFieldDisplayValue('email')}
              onChange={onChangeSalesRepField('email')}
              disabled={true}
            />
          }
          onClear={() => onClearSalesRepField('email')}
        />
        <ClearableInput
          width="30%"
          changed={Boolean(overrideFields?.salesRep?.phoneNumber)}
          input={
            <Input
              label="Sales Rep Phone"
              placeholder={fields?.salesRep?.phoneNumber}
              value={getSalesRepFieldDisplayValue('phoneNumber')}
              onChange={onChangeSalesRepField('phoneNumber')}
              disabled={true}
            />
          }
          onClear={() => onClearSalesRepField('phoneNumber')}
        />
      </FlexContainer>
      <DividerLine margin="20px 0" height="2px" />
      <SectionTitle>
        <span>2</span>Menu Details
      </SectionTitle>
      <YSpacing height="10px" />
      {Object.keys(fields?.menuItemsMap || {})
        .filter((menuItemGroupType) => {
          const groupKey =
            menuItemGroupType as keyof EventProposalPDFParams['menuItemsMap']
          if (
            fields.menuItemsMap[groupKey] &&
            fields.menuItemsMap[groupKey].items.length > 0
          ) {
            return true
          }

          return false
        })
        .map((menuItemGroupType) => {
          const groupKey =
            menuItemGroupType as keyof EventProposalPDFParams['menuItemsMap']

          return (
            <>
              <MenuItemsSection
                key={menuItemGroupType}
                groupKey={groupKey}
                fields={fields.menuItemsMap[groupKey]}
                hiddenMenuItems={overrideFields?.hiddenMenuItems}
                overrideFields={getOverrideMenuItemGroupFields(groupKey)}
                onChangeMenuItemGroupField={onChangeMenuItemGroupField(
                  groupKey,
                )}
                onClearMenuItemGroupField={onClearMenuItemGroupField(groupKey)}
                onChangeMenuItemField={onChangeMenuItemField(groupKey)}
                onClearMenuItemField={onClearMenuItemField(groupKey)}
                onToggleHiddenMenuItem={onToggleHiddenMenuItem}
              />
              <YSpacing height="10px" />
            </>
          )
        })}
      <DividerLine margin="20px 0" height="2px" />
      <SectionTitle>
        <span>3</span>Service Items
      </SectionTitle>
      <YSpacing height="10px" />
      {serviceItemGroups.map((groupKey) => (
        <>
          <ServiceItemsSection
            key={groupKey}
            groupKey={groupKey}
            serviceItemText={fields[groupKey]}
            overrideText={overrideFields?.[groupKey]}
            onChangeServiceItemText={onFieldChange(groupKey)}
            onClearServiceItemText={() => onClearField(groupKey)}
          />
          <YSpacing height="10px" />
        </>
      ))}
      <DividerLine margin="20px 0" height="2px" />
      <SectionTitle>
        <span>4</span>Pricing
      </SectionTitle>
      <YSpacing height="10px" />
      {pricingFields.map((field, idx) => (
        <>
          <ClearableInput
            key={`${field}-${idx}`}
            width="50%"
            changed={Boolean(overrideFields?.[field])}
            input={
              <Input
                label={startCase(field)}
                placeholder={
                  fields?.[field as keyof EventProposalPDFParams]?.toString() ||
                  ''
                }
                value={getFieldValue<string>(field) || ''}
                onChange={onFieldChange(field)}
                disabled={true}
              />
            }
            onClear={() => onClearField(field)}
          />
          <YSpacing key={`${field}-${idx}-spacing`} height="10px" />
        </>
      ))}
      <YSpacing height="10px" />
      <FlexContainer width="100%" flexDirection="row" justifyContent="flex-end">
        <Button
          label="Cancel"
          onClick={() => closeEditPDFModal()}
          backgroundColor={colors.gray200}
        />
        <XSpacing width="10px" />
        <Button
          label="Save"
          onClick={onSaveOverrideFields}
          backgroundColor={colors.blue200}
        />
      </FlexContainer>
    </div>
  )
}

interface MenuItemsSectionProps {
  groupKey: keyof EventProposalPDFParams['menuItemsMap']
  fields: MenuItemGroupParams
  overrideFields: OverrideMenuItemGroupParams | undefined
  hiddenMenuItems?: string[]
  onChangeMenuItemGroupField: (
    field: keyof OverrideMenuItemGroupParams,
  ) => (value: string | Record<string, OverrideMenuItemParams>) => void
  onClearMenuItemGroupField: (field: keyof OverrideMenuItemGroupParams) => void
  onChangeMenuItemField: (
    itemId: string,
  ) => (field: keyof OverrideMenuItemParams) => (value: string) => void
  onClearMenuItemField: (
    itemId: string,
  ) => (field: keyof OverrideMenuItemParams) => void
  onToggleHiddenMenuItem: (itemId: string) => void
}

const MenuItemsSection: React.FC<MenuItemsSectionProps> = ({
  groupKey,
  fields,
  overrideFields,
  hiddenMenuItems,
  onChangeMenuItemGroupField,
  onClearMenuItemGroupField,
  onChangeMenuItemField,
  onClearMenuItemField,
  onToggleHiddenMenuItem,
}) => {
  const [expanded, setExpanded] = useState(false)

  const isItemHidden = (itemId: string) => {
    return hiddenMenuItems?.includes(itemId)
  }

  return (
    <FlexContainer
      width="100%"
      flexDirection="column"
      alignItems="flex-start"
      border="1px solid #E0E0E0"
      padding="5px"
    >
      <FlexContainer
        width="100%"
        flexDirection="row"
        alignItems="center"
        justifyContent="flex-start"
      >
        <div
          className="hover:cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? (
            <FaChevronCircleDown size={20} />
          ) : (
            <FaChevronCircleRight size={20} />
          )}
        </div>
        <XSpacing width="5%" />
        <ClearableInput
          width="30%"
          changed={Boolean(overrideFields?.label || false)}
          input={
            <Input
              label={`${startCase(groupKey)} Label`}
              placeholder={fields.label}
              value={overrideFields?.label || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onChangeMenuItemGroupField('label')(e.target.value)
              }
            />
          }
          onClear={() => onClearMenuItemGroupField('label')}
        />
      </FlexContainer>
      {expanded && (
        <FlexContainer
          width="100%"
          flexDirection="column"
          alignItems="flex-start"
        >
          {fields.items.map((item, idx) => (
            <>
              <FlexContainer
                key={item.id}
                width="85%"
                flexDirection="column"
                alignItems="flex-start"
                border={
                  isItemHidden(item.id)
                    ? '2px solid #F56565'
                    : '1px solid #E0E0E0'
                }
                padding="5px"
                margin="5px 0 5px 10%"
              >
                <FlexContainer
                  width="100%"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                >
                  <FlexContainer
                    width="80%"
                    flexDirection="column"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                  >
                    {isItemHidden(item.id) && (
                      <>
                        <span className="text-red-500 font-bold">
                          Hidden On PDF
                        </span>
                        <YSpacing height="5px" />
                      </>
                    )}
                    <ClearableInput
                      width="30%"
                      changed={Boolean(
                        overrideFields?.items?.[item.id]?.name || false,
                      )}
                      input={
                        <Input
                          label={item.name}
                          placeholder={item.name}
                          value={overrideFields?.items?.[item.id]?.name || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            onChangeMenuItemField(item.id)('name')(
                              e.target.value,
                            )
                          }
                        />
                      }
                      onClear={() => onClearMenuItemField(item.id)('name')}
                    />
                  </FlexContainer>
                  <button
                    onClick={() => onToggleHiddenMenuItem(item.id)}
                    className={`px-2 py-1 rounded-md w-1/5 ${
                      isItemHidden(item.id)
                        ? 'bg-green-500 text-white'
                        : 'bg-red-500 text-white'
                    }`}
                  >
                    {isItemHidden(item.id) ? 'Show On PDF' : 'Hide On PDF'}
                  </button>
                </FlexContainer>
                <YSpacing height="5px" />
                <ClearableInput
                  width="100%"
                  changed={Boolean(
                    overrideFields?.items?.[item.id]?.description || false,
                  )}
                  input={
                    <TextArea
                      label="Description"
                      placeholder={item.description}
                      value={
                        overrideFields?.items?.[item.id]?.description || ''
                      }
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onChangeMenuItemField(item.id)('description')(
                          e.target.value,
                        )
                      }
                    />
                  }
                  onClear={() => onClearMenuItemField(item.id)('description')}
                />
              </FlexContainer>
              {idx < fields.items.length - 1 && (
                <DividerLine
                  key={`${item.id}-divider`}
                  margin="10px 0 10px 10%"
                  height="2px"
                  width="85%"
                />
              )}
            </>
          ))}
        </FlexContainer>
      )}
    </FlexContainer>
  )
}

interface ServiceItemsSectionProps {
  groupKey: string
  serviceItemText: string
  overrideText?: string
  onChangeServiceItemText: (e: React.ChangeEvent<HTMLInputElement>) => void
  onClearServiceItemText: () => void
}
const ServiceItemsSection: React.FC<ServiceItemsSectionProps> = ({
  groupKey,
  serviceItemText,
  overrideText,
  onChangeServiceItemText,
  onClearServiceItemText,
}) => {
  return (
    <FlexContainer
      width="100%"
      flexDirection="column"
      alignItems="flex-start"
      border="1px solid #E0E0E0"
      padding="5px"
    >
      <ClearableInput
        width="100%"
        changed={Boolean(overrideText)}
        input={
          <TextArea
            label={startCase(groupKey)}
            placeholder={serviceItemText}
            value={overrideText || ''}
            onChange={onChangeServiceItemText}
            showCharCount={true}
            countPlaceholder={true}
            suggestedMaxLength={90}
          />
        }
        onClear={onClearServiceItemText}
      />
    </FlexContainer>
  )
}

const InfoCopy = styled.p`
  font-family: 'light';
  font-size: 14px;
  color: ${colors.gray400};
  margin-bottom: 20px;
`

const SectionTitle = styled.h2<any>`
  font-family: 'bold';
  color: ${colors.gray400};
  font-size: 20px;
  display: flex;
  align-items: center;
  margin: ${(props) => props.margin};
  span {
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 25px;
    min-width: 25px;
    height: 25px;
    background: ${colors.orange};
    border-radius: 25px;
    margin-right: 10px;
  }
  .details {
    font-family: 'light';
    font-size: 18px;
    display: flex;
    align-items: center;
  }
  .message {
    font-family: 'bold';
    font-size: 14px;
    display: flex;
    align-items: center;
    text-decoration: underline;
  }
  .message:hover {
    cursor: pointer;
  }
`
